import { c as createFocusTrap } from './focus-trap.esm-MYAhwz8v.js';
import { o as onClickOutside } from './onClickOutside-CQ5hkyE7.js';
import { P as Pubsub } from './pubsub-DKqIEqaC.js';
import './notyf-DIw0RvBj.js';

class DropdownElement extends HTMLElement {
  static {
    // Let the document listen for clicks on dropdown controllers
    document.addEventListener('click', (event) => {
      // Check if event and event.target exist and that target is an Element
      if (!event || !event.target || !(event.target instanceof Element)) return;

      const controller = event.target.closest('[data-dropdown-controller]');
      const dropdownId = controller?.getAttribute('aria-controls');
      if (!dropdownId) return;

      const dropdown = document.getElementById(dropdownId);
      if (!dropdown) {
        console.error(`Dropdown with id '${dropdownId}' not found`);
        return;
      }

      dropdown.checkValidLink(controller, event, dropdown);
    });

    // Add mouseenter event listener for desktop to close other dropdowns
    document.addEventListener(
      'mouseenter',
      (event) => {
        // Only apply this on desktop devices
        if (window.innerWidth < 768) return;

        // Check if event and event.target exist and that target is an Element
        if (!event || !event.target || !(event.target instanceof Element)) return;

        const controller = event.target.closest('[data-dropdown-controller]');
        if (!controller) return;

        const dropdownId = controller.getAttribute('aria-controls');
        if (!dropdownId) return;

        // Close all other dropdowns
        document.querySelectorAll('dropdown-element').forEach((dropdown) => {
          if (dropdown.id !== dropdownId && dropdown.ariaHidden === 'false') {
            dropdown.close();
          }
        });
      },
      true
    ); // Use capture phase to ensure this runs before other handlers
  }

  connectedCallback() {
    // Create the focus trap
    this.trap = createFocusTrap(this, {
      // Allow clicks outside the modal to close the dropdown
      allowOutsideClick: true,
      initialFocus: false,
    });

    // Listen for key:escape events
    this.unsubscribeEscapeKey = Pubsub.subscribe('key:escape', () => this.close());

    // Listen for clicks outside the dropdown
    onClickOutside(this, () => this.close());
  }

  disconnectedCallback() {
    this.unsubscribeEscapeKey();
  }

  /**
   * Check if the controller has a valid href (is a link)
   * @param {Element} controller - The controller element
   * @param {Event} event - The event object
   * @param {DropdownElement} dropdown - The dropdown element
   */
  checkValidLink(controller, event, dropdown) {
    // Check if the controller has a valid href (is a link)
    const href = controller.getAttribute('href');
    const hasValidLink = href && href !== '#' && href !== '';

    // If it's a valid link, don't prevent default - let the link work
    // The dropdown will still toggle, but the link will also navigate
    if (hasValidLink) {
      dropdown.handleAction('toggle');
    } else {
      dropdown.handleAction('toggle');
      event.preventDefault();
    }
  }

  /**
   * Handle dropdown actions
   * @param {string} action - The action to perform ('open', 'close', or 'toggle')
   */
  handleAction(action) {
    switch (action) {
      case 'open':
        this.open();
        break;
      case 'close':
        this.close();
        break;
      default:
        this.toggle();
        break;
    }
  }

  /**
   * Toggle the dropdown
   */
  toggle() {
    this.ariaHidden === 'true' ? this.open() : this.close();
  }

  /**
   * Open the dropdown
   */
  open() {
    if (this.ariaHidden === 'false') return;

    // Close all other dropdowns before opening this one
    if (window.innerWidth >= 768) {
      document.querySelectorAll('dropdown-element').forEach((dropdown) => {
        if (dropdown !== this && dropdown.ariaHidden === 'false') {
          dropdown.close();
        }
      });
    }

    this.ariaHidden = false;
    this.updateControllers();
    this.trap.activate();
  }

  /**
   * Close the dropdown
   */
  close() {
    if (this.ariaHidden === 'true') return;
    this.trap.deactivate();
    setTimeout(() => {
      this.ariaHidden = true;
      this.updateControllers();
    }, 100);
  }

  /**
   * Update the aria-expanded attribute on dropdown controllers
   */
  updateControllers() {
    document.querySelectorAll(`[aria-controls="${this.id}"]`).forEach((element) => {
      element.setAttribute('aria-expanded', this.ariaHidden === 'false' ? 'true' : 'false');
    });
  }
}

customElements.define('dropdown-element', DropdownElement);

export { DropdownElement };
